import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Button } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { cx } from "@/lib/tailwind-utils";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { LocationType, SelfScheduleStep } from "@/stores/self-schedule-store";
export const LocationTypeStep = () => {
  const $ = _c(51);
  const {
    setLocationType,
    firstStep,
    setSelectedLocationId,
    selectedLocation
  } = useSelfScheduleContext();
  const {
    patient
  } = usePatientVerification();
  const previousLocationId = patient?.mostRecentAppointmentLocationId;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...selfScheduleQueryOptions.frontPage(),
      select: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: frontPageData
  } = useQuery(t0);
  frontPageData?.locations;
  let t1;
  if ($[1] !== frontPageData?.locations || $[2] !== previousLocationId) {
    t1 = previousLocationId ? frontPageData?.locations?.find(loc => loc.id === previousLocationId)?.isVirtual : null;
    $[1] = frontPageData?.locations;
    $[2] = previousLocationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isExistingLocationVirtual = t1;
  const selectedLocationVirtual = selectedLocation?.isVirtual;
  let t2;
  if ($[4] !== frontPageData?.hasVirtualServices || $[5] !== frontPageData?.locations || $[6] !== setLocationType || $[7] !== setSelectedLocationId) {
    t2 = locationType => {
      setLocationType(locationType);
      if (locationType === LocationType.IN_PERSON) {
        const inPersonLocations = frontPageData?.locations?.filter(_temp3);
        setSelectedLocationId(inPersonLocations?.length === 1 ? inPersonLocations[0].id : null);
        return;
      }
      if (locationType === LocationType.VIRTUAL) {
        const virtualLocations = frontPageData?.locations?.filter(_temp4);
        setSelectedLocationId(virtualLocations?.length === 1 && !frontPageData?.hasVirtualServices ? virtualLocations[0].id : null);
      }
    };
    $[4] = frontPageData?.hasVirtualServices;
    $[5] = frontPageData?.locations;
    $[6] = setLocationType;
    $[7] = setSelectedLocationId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleLocationTypeSelection = t2;
  const t3 = firstStep === SelfScheduleStep.LOCATION_TYPE ? "mt-10 motion-opacity-in-0 motion-duration-1000" : "mt-12 motion-scale-in-105 motion-opacity-in-85";
  let t4;
  if ($[9] !== t3) {
    t4 = cx("mx-auto flex max-w-md flex-col gap-2", "motion-ease-spring-bouncy", t3);
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== firstStep) {
    t5 = firstStep === SelfScheduleStep.LOCATION_TYPE && <p className="-mb-2">Hey there,</p>;
    $[11] = firstStep;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h1>Would you like to be seen in-person or via telehealth?</h1>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = frontPageData?.tenantName;
  let t8;
  if ($[14] !== t7) {
    t8 = <p>{t7} offers both in-person and telehealth appointments.</p>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cx("hover:!bg-primary hover:!text-primary-foreground", "motion-preset-slide-right-sm");
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== handleLocationTypeSelection) {
    t10 = () => handleLocationTypeSelection(LocationType.IN_PERSON);
    $[17] = handleLocationTypeSelection;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isExistingLocationVirtual || $[20] !== selectedLocationVirtual) {
    t11 = (isExistingLocationVirtual === false || selectedLocationVirtual === false) && <div className={cx("absolute -right-4 -top-4 h-fit text-nowrap rounded-full border px-2 py-0.5", "bg-primary text-caption text-primary-foreground", "motion-preset-slide-right-sm", "motion-delay-700")}><span>{`Your ${isExistingLocationVirtual === false ? "last appointment was" : "selected location is"} In-Person`}</span></div>;
    $[19] = isExistingLocationVirtual;
    $[20] = selectedLocationVirtual;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span>In-Person</span>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t11) {
    t13 = <Button variant="secondary" className={t9} onClick={t10}>{t11}{t12}</Button>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== handleLocationTypeSelection) {
    t14 = () => handleLocationTypeSelection(LocationType.VIRTUAL);
    $[26] = handleLocationTypeSelection;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cx("hover:!bg-primary hover:!text-primary-foreground", "motion-preset-slide-right-sm", "relative");
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== isExistingLocationVirtual || $[30] !== selectedLocationVirtual) {
    t16 = (isExistingLocationVirtual === true || selectedLocationVirtual === true) && <div className={cx("absolute -right-4 -top-4 h-fit text-nowrap rounded-full border px-2 py-0.5 md:-top-2", "bg-primary text-caption text-primary-foreground", "motion-preset-slide-right-sm", "motion-delay-700")}><span>{`Your ${isExistingLocationVirtual === true ? "last appointment was" : "selected location is"} telehealth.`}</span></div>;
    $[29] = isExistingLocationVirtual;
    $[30] = selectedLocationVirtual;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span>Telehealth</span>;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t14 || $[34] !== t16) {
    t18 = <Button variant="secondary" onClick={t14} className={t15}>{t16}{t17}</Button>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== handleLocationTypeSelection) {
    t19 = () => handleLocationTypeSelection(LocationType.ALL);
    $[36] = handleLocationTypeSelection;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cx("hover:!bg-transparent hover:text-foreground", "motion-preset-slide-right-sm", "text-caption text-muted-foreground", "relative");
    t21 = <span>Skip, I'll decide later</span>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t20 = $[38];
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t19) {
    t22 = <Button variant="ghost" onClick={t19} className={t20}>{t21}</Button>;
    $[40] = t19;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t13 || $[43] !== t18 || $[44] !== t22) {
    t23 = <div className="mt-2 flex flex-col gap-2">{t13}{t18}{t22}</div>;
    $[42] = t13;
    $[43] = t18;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t23 || $[47] !== t4 || $[48] !== t5 || $[49] !== t8) {
    t24 = <div data-testid="step-location-type" className={t4}>{t5}{t6}{t8}{t23}</div>;
    $[46] = t23;
    $[47] = t4;
    $[48] = t5;
    $[49] = t8;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
};
function _temp(service) {
  return service.isVirtual;
}
function _temp2(data) {
  return {
    tenantName: data?.tenantName,
    locations: data?.locations,
    hasVirtualServices: data?.services?.some(_temp) ?? false
  };
}
function _temp3(loc_0) {
  return !loc_0.isVirtual;
}
function _temp4(loc_1) {
  return loc_1.isVirtual;
}